import { useState, useMemo } from 'react'
import { Badge, Button, Popover, Tabs, Tag, Tooltip } from 'antd'
import {
  AlertOutlined,
  BellOutlined,
  CheckCircleOutlined,
  CloudOutlined,
  CloudSyncOutlined,
  FileSearchOutlined,
  RadarChartOutlined,
  SendOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { NOTIFICATIONS, type NotificationItem, type NotificationType } from '@/mocks/notifications'

const TYPE_ICON: Record<NotificationType, React.ReactNode> = {
  alert: <AlertOutlined />,
  approval: <FileSearchOutlined />,
  sync: <CloudSyncOutlined />,
  weather: <CloudOutlined />,
  sch: <RadarChartOutlined />,
  system: <SettingOutlined />,
  plan: <SendOutlined />,
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#ef4444',
  warning: '#f59e0b',
  info: '#06b6d4',
  success: '#10b981',
}

function fmtAgo(iso: string): string {
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
  if (diff < 60) return `${diff} giây`
  if (diff < 3600) return `${Math.floor(diff / 60)} phút`
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ`
  return `${Math.floor(diff / 86400)} ngày`
}

export function NotificationCenter() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>(NOTIFICATIONS)
  const [tab, setTab] = useState<'all' | 'unread' | 'critical'>('all')

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items])

  const filtered = useMemo(() => {
    if (tab === 'unread') return items.filter((n) => !n.read)
    if (tab === 'critical') return items.filter((n) => n.severity === 'critical' || n.severity === 'warning')
    return items
  }, [items, tab])

  const markAllRead = () => {
    setItems((list) => list.map((n) => ({ ...n, read: true })))
  }

  const handleClick = (n: NotificationItem) => {
    setItems((list) => list.map((it) => (it.id === n.id ? { ...it, read: true } : it)))
    if (n.link) {
      setOpen(false)
      navigate(n.link)
    }
  }

  const content = (
    <div className="w-[400px] max-w-[90vw]">
      {/* Header */}
      <div className="flex items-center justify-between mb-1 pb-2 border-b border-[var(--color-border-subtle)]">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold">Thông báo</span>
          {unreadCount > 0 && (
            <Tag color="red" className="!m-0 !text-[10px]">
              {unreadCount} mới
            </Tag>
          )}
        </div>
        <Button type="link" size="small" onClick={markAllRead} disabled={unreadCount === 0} className="!px-1">
          Đánh dấu đã đọc
        </Button>
      </div>

      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as typeof tab)}
        size="small"
        className="!mb-1"
        items={[
          { key: 'all', label: `Tất cả (${items.length})` },
          { key: 'unread', label: `Chưa đọc (${unreadCount})` },
          { key: 'critical', label: 'Quan trọng' },
        ]}
      />

      <div className="max-h-[460px] overflow-y-auto -mr-1 pr-1">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-[var(--color-text-muted)]">
            <CheckCircleOutlined style={{ fontSize: 32, marginBottom: 8, color: '#10b981' }} />
            <span className="text-[12px]">Không có thông báo nào</span>
          </div>
        ) : (
          <div className="space-y-1">
            {filtered.map((n) => {
              const color = SEVERITY_COLOR[n.severity]
              return (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className="w-full text-left px-2 py-2 rounded hover:bg-[var(--color-bg-elevated)] transition-colors flex items-start gap-2.5 group"
                  style={{
                    backgroundColor: !n.read ? `${color}0d` : undefined,
                  }}
                >
                  <div
                    className="w-7 h-7 rounded flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: `${color}22`, color }}
                  >
                    {TYPE_ICON[n.type]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[12px] font-semibold text-[var(--color-text-primary)] leading-tight">
                        {n.title}
                      </div>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ backgroundColor: color }} />
                      )}
                    </div>
                    <div className="text-[11px] text-[var(--color-text-secondary)] leading-snug mt-0.5 line-clamp-2">
                      {n.body}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Tag className="!m-0 !text-[10px] !leading-3 !py-0">{n.module}</Tag>
                      <span className="text-[10px] text-[var(--color-text-muted)]">{fmtAgo(n.createdAt)} trước</span>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="pt-2 border-t border-[var(--color-border-subtle)] mt-2 text-center">
        <Button type="link" size="small">
          Xem tất cả thông báo
        </Button>
      </div>
    </div>
  )

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
    >
      <Tooltip title={open ? '' : `Thông báo (${unreadCount} chưa đọc)`}>
        <Badge count={unreadCount} size="small" offset={[-4, 4]} overflowCount={99}>
          <Button type="text" icon={<BellOutlined />} className="!text-[var(--color-text-secondary)]" />
        </Badge>
      </Tooltip>
    </Popover>
  )
}
